import { Card, Tag, Button, Tooltip, notification, Typography } from 'antd';
import { MenuFoldOutlined, MenuUnfoldOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { useState } from 'react';
import GenericToggle from '../GenericToggle/GenericToggle';
import { NODE_CATEGORIES, TRIGGER_TEMPLATES } from '../../constants/alkymation';
import { updateWorkflow } from '../../API/services/workflowsService';
import { t } from '../../i18n/i18n';
import WorkflowExecutions from '../WorkflowExecutions/WorkflowExecutions';
import ConfirmModal from '../ConfirmModal/ConfirmModal';
import './WorkflowStatus.scss';

const WorkflowStatus = ({ workflow, collapsed, setCollapsed }) => {
  const [updating, setUpdating] = useState(false);
  const [showToggleConfirm, setShowToggleConfirm] = useState(false);
  const [pendingActiveState, setPendingActiveState] = useState(null);
  const { Title } = Typography;

  const handleToggleClick = (newValue) => {
    // Check if workflow is saved
    if (!workflow?._id) {
      notification.warning({
        message: t('ALKYMATION_WORKFLOW_NOT_SAVED_TITLE'),
        description: t('ALKYMATION_WORKFLOW_NOT_SAVED_MESSAGE'),
        placement: 'topRight',
      });
      setPendingActiveState(false);
      return;
    }

    setPendingActiveState(newValue);
    setShowToggleConfirm(true);
  };

  const confirmToggleActive = async () => {
    if (!workflow?._id || pendingActiveState === null) return;

    try {
      setUpdating(true);
      const workflowId = workflow._id;
      await updateWorkflow(workflowId, { ...workflow, active: pendingActiveState });

      // Update the workflow state (this should ideally be handled by parent component)
      workflow.active = pendingActiveState;

      notification.success({
        message: pendingActiveState
          ? t('ALKYMATION_WORKFLOW_ACTIVATED')
          : t('ALKYMATION_WORKFLOW_DEACTIVATED'),
        placement: 'topRight',
      });
    } catch (error) {
      notification.error({
        message: t('ALKYMATION_ERROR_UPDATING_WORKFLOW'),
        placement: 'topRight',
      });
    } finally {
      setUpdating(false);
      setPendingActiveState(null);
    }
  };

  const getTriggerInfo = () => {
    const triggerNode = workflow?.nodes?.find(
      (node) => node.template?.category === NODE_CATEGORIES.TRIGGER
    );

    if (!triggerNode) {
      return { type: 'none', status: t('ALKYMATION_WORKFLOW_NOT_CONFIGURED') };
    }

    switch (triggerNode.templateId) {
      case TRIGGER_TEMPLATES.WEBHOOK_SCREENING_COMPLETE:
        return {
          type: 'webhook',
          status: triggerNode.parameters?.screeningId
            ? t('ALKYMATION_WORKFLOW_CONFIGURED')
            : t('ALKYMATION_WORKFLOW_WITHOUT_EVALUATION'),
          icon: '🎯',
        };
      case 'schedule-cron':
        return {
          type: 'schedule',
          status: triggerNode.parameters?.cronExpression || t('ALKYMATION_WORKFLOW_NOT_SCHEDULED'),
          icon: '⏰',
        };
      default:
        return {
          type: 'manual',
          status: t('ALKYMATION_WORKFLOW_MANUAL'),
          icon: '▶️',
        };
    }
  };

  const triggerInfo = getTriggerInfo();
  const workflowId = workflow?._id;

  if (collapsed) {
    const isConfigured = triggerInfo.status === t('ALKYMATION_WORKFLOW_CONFIGURED');

    return (
      <>
        <div className="status-sidebar-collapsed d-flex flex-column h-100">
          <div className="collapsed-header mb-2">
            <Tooltip title={t('ALKYMATION_EXPAND_SIDEBAR')} placement="left">
              <Button
                type="text"
                icon={<MenuFoldOutlined />}
                onClick={() => setCollapsed(false)}
                className="collapse-btn"
                size="large"
              />
            </Tooltip>
          </div>

          <div className="collapsed-content d-flex flex-column justify-content-center align-items-center flex-grow-1 px-2">
            <Tooltip
              title={
                !workflow?._id ? (
                  <div>
                    <strong>{t('ALKYMATION_WORKFLOW_NOT_SAVED_TITLE')}</strong>
                    <br />
                    {t('ALKYMATION_WORKFLOW_NOT_SAVED_MESSAGE')}
                  </div>
                ) : (
                  <div>
                    <strong>{t('ALKYMATION_WORKFLOW_STATUS_TITLE')}</strong>
                    <br />
                    {workflow?.active
                      ? t('ALKYMATION_WORKFLOW_ACTIVE')
                      : t('ALKYMATION_WORKFLOW_INACTIVE')}
                  </div>
                )
              }
              placement="left"
            >
              <div
                className={`collapsed-card text-center p-2 rounded ${
                  !workflow?._id
                    ? 'disabled-status'
                    : workflow?.active
                    ? 'active-status'
                    : 'inactive-status'
                }`}
                onClick={() => workflow?._id && handleToggleClick(!workflow?.active)}
                style={{
                  cursor: workflow?._id ? 'pointer' : 'not-allowed',
                  opacity: workflow?._id ? 1 : 0.5,
                }}
              >
                <PlayCircleOutlined
                  className={`d-block mb-1 ${
                    !workflow?._id ? 'text-muted' : workflow?.active ? 'text-success' : 'text-muted'
                  }`}
                  style={{ fontSize: '18px' }}
                />
                <span className="collapsed-label">
                  {!workflow?._id
                    ? t('ALKYMATION_NOT_SAVED')
                    : workflow?.active
                    ? t('ALKYMATION_ACTIVE')
                    : t('ALKYMATION_INACTIVE')}
                </span>
              </div>
            </Tooltip>

            <Tooltip
              title={
                <div>
                  <strong>{t('ALKYMATION_WORKFLOW_TRIGGER')}:</strong> {triggerInfo.type}
                  <br />
                  {triggerInfo.status}
                </div>
              }
              placement="left"
            >
              <div className="collapsed-card text-center p-2 rounded bg-light">
                <span className="d-block mb-1" style={{ fontSize: '16px' }}>
                  {triggerInfo.icon}
                </span>
                <span className="collapsed-label text-muted">
                  {triggerInfo.type.substring(0, 6).toUpperCase()}
                </span>
              </div>
            </Tooltip>

            <Tooltip
              title={
                <div>
                  <strong>{t('ALKYMATION_CONFIGURATION')}:</strong>
                  <br />
                  {triggerInfo.status}
                </div>
              }
              placement="left"
            >
              <div
                className={`collapsed-card text-center p-2 rounded ${
                  isConfigured ? 'config-ready' : 'config-pending'
                }`}
              >
                <div
                  className={`status-dot mx-auto mb-1 ${
                    isConfigured ? 'dot-success' : 'dot-warning'
                  }`}
                />
                <span className="collapsed-label">
                  {isConfigured
                    ? t('ALKYMATION_WORKFLOW_CONFIGURED').slice(0, 6).toUpperCase()
                    : t('ALKYMATION_STATUS_PENDING').slice(0, 4).toUpperCase()}
                </span>
              </div>
            </Tooltip>
          </div>

          {workflowId && (
            <div className="collapsed-executions mt-auto border-top pt-2 px-2">
              <WorkflowExecutions workflowId={workflowId} collapsed />
            </div>
          )}
        </div>

        {showToggleConfirm && (
          <div
            className="confirm-modal-overlay position-fixed d-flex align-items-center justify-content-center"
            onClick={() => setShowToggleConfirm(false)}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="confirm-modal-wrapper position-static"
            >
              <ConfirmModal
                show={showToggleConfirm}
                setShow={setShowToggleConfirm}
                callback={confirmToggleActive}
                withoutDontAsk
                title={
                  pendingActiveState
                    ? t('ALKYMATION_CONFIRM_ACTIVATE_WORKFLOW')
                    : t('ALKYMATION_CONFIRM_DEACTIVATE_WORKFLOW')
                }
                text={workflow?.name || t('ALKYMATION_WITHOUT_NAME')}
                cautionText={
                  pendingActiveState
                    ? t('ALKYMATION_ACTIVATE_WORKFLOW_WARNING')
                    : t('ALKYMATION_DEACTIVATE_WORKFLOW_WARNING')
                }
                confirmButtonText={
                  pendingActiveState ? t('ALKYMATION_ACTIVATE') : t('ALKYMATION_DEACTIVATE')
                }
                cancelButtonText={t('ALKYMATION_CANCEL')}
              />
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <div className="status-sidebar-panel">
        <div className="status-header">
          <Tooltip title={t('ALKYMATION_COLLAPSE_SIDEBAR')} placement="left">
            <Button
              type="text"
              icon={<MenuUnfoldOutlined />}
              onClick={() => setCollapsed(true)}
              className="collapse-btn"
              size="large"
            />
          </Tooltip>
        </div>
        <Card
          // title={t('ALKYMATION_WORKFLOW_STATUS_TITLE')}
          className="workflow-status-card"
          extra={
            <div className="d-flex flex-column align-items-center">
              <Title level={3}>{t('ALKYMATION_WORKFLOW_STATUS_TITLE')}</Title>
              <div className="d-flex gap-2 justify-content-center">
                <GenericToggle
                  defaultActive={workflow?.active || false}
                  value={workflow?.active || false}
                  actionFunction={() => {}}
                  actionFunctionValue={handleToggleClick}
                  loading={updating}
                />
                {!workflow?._id ? (
                  <Tag color="warning">{t('ALKYMATION_NOT_SAVED')}</Tag>
                ) : workflow?.active ? (
                  <Tag color="success">{t('ALKYMATION_WORKFLOW_ACTIVE')}</Tag>
                ) : (
                  <Tag color="default">{t('ALKYMATION_WORKFLOW_INACTIVE')}</Tag>
                )}
              </div>
            </div>
          }
        >
          <div className="workflow-trigger mb-3">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <strong>{t('ALKYMATION_WORKFLOW_TRIGGER')}:</strong>{' '}
                <span className="trigger-type">
                  {triggerInfo.icon} {triggerInfo.type}
                </span>
                {triggerInfo.status && (
                  <Tag
                    className="ml-2"
                    color={
                      triggerInfo.status === t('ALKYMATION_WORKFLOW_CONFIGURED')
                        ? 'success'
                        : 'warning'
                    }
                  >
                    {triggerInfo.status}
                  </Tag>
                )}
              </div>
            </div>
          </div>
        </Card>

        {workflowId && <WorkflowExecutions workflowId={workflowId} />}
      </div>

      {showToggleConfirm && (
        <div
          className="confirm-modal-overlay position-fixed d-flex align-items-center justify-content-center"
          onClick={() => setShowToggleConfirm(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="confirm-modal-wrapper position-static"
          >
            <ConfirmModal
              show={showToggleConfirm}
              setShow={setShowToggleConfirm}
              callback={confirmToggleActive}
              withoutDontAsk
              title={
                pendingActiveState
                  ? t('ALKYMATION_CONFIRM_ACTIVATE_WORKFLOW')
                  : t('ALKYMATION_CONFIRM_DEACTIVATE_WORKFLOW')
              }
              text={workflow?.name || t('ALKYMATION_WITHOUT_NAME')}
              cautionText={
                pendingActiveState
                  ? t('ALKYMATION_ACTIVATE_WORKFLOW_WARNING')
                  : t('ALKYMATION_DEACTIVATE_WORKFLOW_WARNING')
              }
              confirmButtonText={
                pendingActiveState ? t('ALKYMATION_ACTIVATE') : t('ALKYMATION_DEACTIVATE')
              }
              cancelButtonText={t('ALKYMATION_CANCEL')}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default WorkflowStatus;
